<template>
  <div>
    <b-row>
      <b-col>
        <b-card>
          <!-- HEADER -->
          <b-row class="mb-3 align-items-center">
            <b-col>
              <h3>Minimax</h3>
              <small class="text-muted">Current user and organisations</small>
            </b-col>
          </b-row>

          <!-- ERROR BANNER -->
          <b-row v-if="settingsError || profileError || orgsError">
            <b-col>
              <b-alert
                variant="danger"
                show
                dismissible
                @dismissed="
                  settingsError = null;
                  profileError = null;
                  orgsError = null;
                "
              >
                {{ settingsError || profileError || orgsError }}
              </b-alert>
            </b-col>
          </b-row>

          <!-- LOADING STATES -->
          <b-row v-if="settingsLoading">
            <b-col class="text-center">
              <b-spinner small label="Loading settings…" /> Loading settings…
            </b-col>
          </b-row>

          <b-row v-else>
            <b-col>
              <!-- PROFILE -->
              <b-card class="mb-3">
                <template #header>
                  <strong>Current User</strong>
                </template>

                <div v-if="profileLoading" class="text-center my-2">
                  <b-spinner small label="Loading profile…" /> Loading profile…
                </div>

                <div v-else-if="userProfile">
                  <b-row>
                    <b-col cols="12" md="6">
                      <div>
                        <strong>Full name:</strong> {{ userProfile.FullName }}
                      </div>
                      <div><strong>Email:</strong> {{ userProfile.Email }}</div>
                      <div>
                        <strong>Mobile:</strong>
                        {{ userProfile.MobilePhone || "-" }}
                      </div>
                    </b-col>
                    <b-col cols="12" md="6">
                      <div>
                        <strong>Language:</strong> {{ userProfile.Language }}
                      </div>
                      <div>
                        <strong>User ID:</strong> {{ userProfile.UserId }}
                      </div>
                    </b-col>
                  </b-row>
                </div>

                <div v-else class="text-muted">No profile data.</div>
              </b-card>

              <!-- ORGANISATIONS -->
              <b-card>
                <template #header>
                  <strong>Organisations</strong>
                </template>

                <div v-if="orgsLoading" class="text-center my-2">
                  <b-spinner small label="Loading organisations…" /> Loading
                  organisations…
                </div>

                <div v-else>
                  <div v-if="orgs.length === 0" class="text-muted">
                    No organisations found.
                  </div>
                  <b-table
                    v-else
                    small
                    striped
                    hover
                    :items="orgs"
                    :fields="orgTableFields"
                    responsive="sm"
                  >
                    <template #cell(name)="{ item }">
                      <router-link :to="dashboardRoute(item)">
                        {{ item.Organisation.Name }}
                      </router-link>
                    </template>
                    <template #cell(id)="{ item }">
                      {{ item.Organisation.ID }}
                    </template>
                    <template #cell(apiAccess)="{ item }">
                      {{ item.APIAccess }}
                    </template>
                    <template #cell(mobileAccess)="{ item }">
                      {{ item.MobileAccess }}
                    </template>
                  </b-table>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Axios from "axios";
import {
  BSidebar,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BAlert,
  BSpinner,
  BTable,
} from "bootstrap-vue";

const ENVIRONMENT = "dev";

export default {
  name: "MinimaxOverview",
  components: {
    BSidebar,
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
    BSpinner,
    BTable,
  },

  data() {
    return {
      // existing state
      settingsLoading: false,
      settingsError: null,
      userId: null,

      // profile & orgs state
      profileLoading: false,
      profileError: null,
      userProfile: null,

      orgsLoading: false,
      orgsError: null,
      orgs: [],

      orgTableFields: [
        { key: "name", label: "Name" },
        { key: "id", label: "ID" },
        { key: "apiAccess", label: "API Access" },
        { key: "mobileAccess", label: "Mobile Access" },
      ],
    };
  },

  computed: {
    projectUUID() {
      return this.$store.getters.getCurrentProject.uuid;
    },
  },

  created() {
    this.fetchSettings();
  },

  methods: {
    async fetchSettings() {
      this.settingsLoading = true;
      this.settingsError = null;

      try {
        const resp = await Axios.get(
          `http://localhost:3001/v1/minimax/settings`,
          {
            headers: {
              "x-env": ENVIRONMENT,
              "x-project-uuid": this.projectUUID,
            },
          }
        );
        this.userId = resp.data.userId;
        if (!this.userId) {
          this.settingsError =
            "Missing userId. Configure Minimax settings first.";
          return;
        }

        await Promise.all([this.fetchProfile(), this.fetchOrgs()]);
      } catch (err) {
        console.error("Error fetching settings:", err);
        this.settingsError =
          err.response.data.message ||
          err.message ||
          "Failed to load settings.";
      } finally {
        this.settingsLoading = false;
      }
    },

    async fetchProfile() {
      if (!this.userId) return;
      this.profileLoading = true;
      this.profileError = null;
      try {
        const resp = await Axios.get(
          `http://localhost:3001/v1/minimax/${this.userId}/api/currentuser/profile`,
          {
            headers: {
              "x-env": ENVIRONMENT,
              "x-project-uuid": this.projectUUID,
            },
          }
        );
        this.userProfile = resp.data || null;
      } catch (err) {
        console.error("Error fetching profile:", err);
        this.profileError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to load profile.";
      } finally {
        this.profileLoading = false;
      }
    },

    async fetchOrgs() {
      if (!this.userId) return;
      this.orgsLoading = true;
      this.orgsError = null;
      try {
        const resp = await Axios.get(
          `http://localhost:3001/v1/minimax/${this.userId}/api/currentuser/orgs`,
          {
            headers: {
              "x-env": ENVIRONMENT,
              "x-project-uuid": this.projectUUID,
            },
          }
        );
        const data = resp.data || {};
        this.orgs = Array.isArray(data.Rows) ? data.Rows : [];
      } catch (err) {
        console.error("Error fetching organisations:", err);
        this.orgsError =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          "Failed to load organisations.";
      } finally {
        this.orgsLoading = false;
      }
    },

    dashboardRoute(item) {
      const orgId = item && item.Organisation && item.Organisation.ID;
      return {
        path: this.$route.path,
        query: { ...this.$route.query, orgId },
      };
    },
  },
};
</script>

<style scoped></style>
